import React, { useEffect, useState } from "react";
import { LogIn } from "lucide-react";
import { apiGet, apiPost, clearApiAuth, saveApiSettings } from "../Api/Client.js";
import { returnAddress } from "../Api/returnAddress.js";
import { DEFAULT_LOGO as atgLogoPng } from "../Components/icons/icons.jsx";
import { Alert, Button, Card, Field, Input, PasswordInput, Spinner } from "../Ui/index.jsx";
import "../Styles/LoginScreen.css";

export function LoginScreen({ settings, onLogin }) {
  const [form, setForm] = useState({
    apiBase: settings.apiBase,
    tenantId: settings.tenantId || "1",
    workspaceId: settings.workspaceId || "1",
    identifier: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm((current) => ({
      ...current,
      apiBase: settings.apiBase,
      tenantId: settings.tenantId || "1",
      workspaceId: settings.workspaceId || "1",
    }));
  }, [settings]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.identifier || !form.password) {
      setError("Enter Your Username And Password.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const loginSettings = {
        apiBase: form.apiBase,
        tenantId: form.tenantId,
        workspaceId: form.workspaceId,
      };
      saveApiSettings(loginSettings);
      // The password goes to the server once; the reply sets the session cookie that every
      // later call carries. Nothing of it stays in the browser (W06-F01).
      const currentUser = await apiPost("/Users/Auth/Login/", {
        identifier: form.identifier,
        // Keep the legacy field during rolling deployments. Old backends use
        // username; Identity V2 prefers identifier.
        username: form.identifier,
        password: form.password,
        tenant_id: form.tenantId ? parseInt(form.tenantId, 10) || null : null,
        workspace_id: form.workspaceId ? parseInt(form.workspaceId, 10) || null : null,
      });
      saveApiSettings({
        ...loginSettings,
        signedIn: true,
        tenantId: currentUser?.activeTenant?.id || form.tenantId,
        workspaceId: currentUser?.activeWorkspace?.id || form.workspaceId,
      });
      // Sent here by another app's sign-in step: go back to it instead of home.
      const back = returnAddress(window.location.search, window.location.origin);
      if (back) {
        window.location.assign(back);
        return;
      }
      if (!currentUser.user?.isSuperuser) {
        const employee = currentUser.employees?.[0];
        if (employee) {
          const employeeProfile = await apiGet(`/Users/EmployeeProfiles/${employee.id}/`);
          if (!employeeProfile.onboarding_completed) {
            onLogin("/onboarding/");
            return;
          }
        }
      }
      onLogin();
    } catch (loginError) {
      clearApiAuth();
      setError(loginError?.status === 401 ? "Invalid Username Or Password." : loginError?.message || "Login Failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="Login-Page">
      <div className="Login-Container">
        <header className="Login-Brand">
          <img src={atgLogoPng} alt="Banao Technologies" width="48" height="48" />
          <h1 className="ds-h2">Intranet</h1>
          <p className="ds-card__sub">Welcome Back — Sign In To Your Workspace</p>
        </header>

        <Card>
          <form className="Login-Form" onSubmit={submit}>
            {error && <Alert tone="danger">{error}</Alert>}
            <Field label="Username">
              <Input autoFocus value={form.identifier} onChange={(e) => update("identifier", e.target.value)} autoComplete="username" placeholder="Enter your username or account" />
            </Field>
            <Field label="Password">
              <PasswordInput value={form.password} onChange={(e) => update("password", e.target.value)} autoComplete="current-password" placeholder="Enter your password" />
            </Field>
            <Button variant="primary" type="submit" disabled={submitting} leftIcon={submitting ? <Spinner label="Signing in" /> : <LogIn size={14} />}>
              {submitting ? "Signing In..." : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    </main>
  );
}
